import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    linkedSignal,
    output,
    ViewEncapsulation,
} from "@angular/core";
import { GridListItem, GridsService, PutGridListItem } from "../../services/grids.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { LynxService } from "../../services/lynx.service";
import { FormField, form, hidden, readonly, required, submit } from "@angular/forms/signals";
import { s25Resource } from "../../util/s25.resource";
import { S25Assert } from "../../util/s25.assert";
import { Bind } from "../../decorators/bind.decorator";
import { NgTemplateOutlet } from "@angular/common";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25ToggleButtonModule } from "../s25-toggle-button/s25.toggle.button.module";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { AcademicGrid } from "./s25.academic.grid";

@Component({
    selector: "s25-ng-create-academic-grid",
    template: `
        <form (submit)="onSubmit()">
            <label for="createAcademicGridName">Name <span class="required">*</span></label>
            <div class="input-wrapper">
                <input id="createAcademicGridName" [formField]="form.name" class="c-input" />
                <ng-container [ngTemplateOutlet]="errors" [ngTemplateOutletContext]="{ field: form.name }" />
            </div>

            <label>
                Event Search
                @if (!form.eventQuery().readonly()) {
                    <span class="required">*</span>
                }
            </label>
            <div class="input-wrapper">
                @if (!form.eventQuery().readonly()) {
                    <s25-ng-search-dropdown [type]="'event'" [allowNonQueryId]="false" [formField]="form.eventQuery" />
                    <ng-container [ngTemplateOutlet]="errors" [ngTemplateOutletContext]="{ field: form.eventQuery }" />
                } @else {
                    <span>{{ form.eventQuery().value()?.itemName }}</span>
                }
            </div>

            @if (!form.locationQuery().hidden()) {
                <label>Location Search </label>
                <div class="input-wrapper">
                    <s25-ng-search-dropdown
                        [type]="'location'"
                        [allowNonQueryId]="false"
                        [formField]="form.locationQuery"
                        [hasNoneItem]="true"
                        [noneItemLabel]="'None'"
                    />
                    <ng-container
                        [ngTemplateOutlet]="errors"
                        [ngTemplateOutletContext]="{ field: form.locationQuery }"
                    />
                </div>
            }

            @if (showEditing()) {
                <label>Editing </label>
                <div class="checkboxes">
                    @if (!form.editing.schedule().hidden()) {
                        <s25-ng-checkbox [formField]="form.editing.schedule">Schedule</s25-ng-checkbox>
                    }
                    @if (!form.editing.location().hidden()) {
                        <s25-ng-checkbox [formField]="form.editing.location">Location Assignments</s25-ng-checkbox>
                    }
                </div>
            }

            <label>Default Standard Schedule</label>
            <s25-ng-dropdown-search-criteria
                [type]="'standardSchedules'"
                [placeholder]="'Standard Schedules'"
                [emptyText]="'No Standard Schedules'"
                [hasNoneItem]="true"
                [noneItemLabel]="'No Schedule'"
                [formField]="form.defaultSchedule"
            />
        </form>

        <div class="footer">
            <s25-ng-button [type]="'primary'" [onClick]="onSubmit">Save</s25-ng-button>
            <s25-ng-button [type]="'danger--outline'" (clicked)="done.emit(undefined)">Cancel</s25-ng-button>
        </div>

        <ng-template #errors let-field="field">
            @if (field().invalid() && (field().dirty() || field().touched())) {
                @for (error of field().errors(); track error.key) {
                    <p class="error-message">{{ error.message }}</p>
                }
            }
        </ng-template>
    `,
    styles: `
        :host {
            display: grid;
            align-content: space-between;
            min-height: 25rem; /* for the search dropdown... */
        }

        form {
            display: grid;
            grid-template-columns: 15ch 1fr;
            grid-column-gap: 1rem;
            grid-row-gap: 0.5rem;
        }

        label {
            align-content: center;
            font-weight: bold;
        }

        .input-wrapper {
            display: grid;
            max-width: 20em;
        }

        .error-message {
            color: var(--error-message-color);
            font-size: 0.8em;
        }

        .footer {
            display: flex;
            justify-content: right;
            gap: 0.5rem;
        }

        .checkboxes {
            display: grid;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormField,
        NgTemplateOutlet,
        S25DropdownPaginatedModule,
        S25ToggleButtonModule,
        S25RadioModule,
        S25ButtonComponent,
        S25CheckboxModule,
    ],
})
export class S25CreateAcademicGridComponent {
    readonly grid = input<(Partial<GridListItem> & Pick<GridListItem, "id">) | undefined>(undefined);
    readonly mode = input<"create" | "edit">("create");

    readonly done = output<GridListItem | undefined>();

    readonly formModel = linkedSignal(() => {
        const scheduleId = this.grid()?.defaultSchedule;
        return {
            name: this.grid()?.name ?? "",
            eventQuery: AcademicGrid.queryToDropdown(this.grid()?.eventQuery!),
            locationQuery: AcademicGrid.queryToDropdown(this.grid()?.locationQuery),
            editing: {
                schedule: this.grid()?.editSchedule ?? true,
                location: this.grid()?.editLocation ?? true,
            },
            defaultSchedule: <DropDownItem<number> | null>(scheduleId ? { itemId: scheduleId } : null),
        };
    });
    readonly form = form(this.formModel, (schemaPath) => {
        required(schemaPath.name, { message: "Enter a name" });
        required(schemaPath.eventQuery, { message: "Select an event search" });
        readonly(schemaPath.eventQuery, () => this.mode() === "edit");
        hidden(schemaPath.editing.schedule, () => !this.expSecEnabled.value());
        hidden(schemaPath.editing.location, () => !this.form.locationQuery().value());
    });
    readonly showEditing = computed(
        () => !this.form.editing.schedule().hidden() || !this.form.editing.location().hidden(),
    );

    readonly expSecEnabled = s25Resource({ defaultValue: false, loader: LynxService.getExpSecEnabled });

    constructor() {
        effect(() => {
            S25Assert(this.mode() !== "edit" || !!this.grid()?.id, "Grid ID is required in edit mode");
        });
    }

    @Bind
    onSubmit() {
        return submit(this.form, this.onSave);
    }

    @Bind
    async onSave(form: typeof this.form) {
        if (!form().dirty()) return this.done.emit(undefined); // Save with no changes => cancel

        const formData = form().value();
        const data: Omit<PutGridListItem, "id"> = {
            name: formData.name,
            type: "academic",
            eventQueryId: formData.eventQuery!.itemId!,
            locationQueryId: formData.locationQuery?.itemId,
            editSchedule: formData.editing.schedule,
            editLocation: formData.editing.location,
            defaultSchedule: formData.defaultSchedule?.itemId,
        };

        if (this.mode() === "create") {
            const [newGrid, err] = await GridsService.create(data);
            if (err) {
                alert("Something went wrong when creating your grid.");
                return;
            }
            this.done.emit(newGrid);
        } else if (this.mode() === "edit") {
            const [newGrid, err] = await GridsService.update({ ...data, id: this.grid()!.id });
            if (err) {
                alert("Something went wrong when updating your grid.");
                return;
            }
            this.done.emit(newGrid);
        }
    }
}
